import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  SearchIcon,
  MessageSquareIcon,
  MailIcon,
  SendIcon,
  PlusIcon,
  XIcon,
  CheckIcon,
  CheckCheckIcon,
  AlertCircleIcon,
  InboxIcon,
  SmartphoneIcon,
  PaperclipIcon,
  ArrowLeftIcon,
  LinkIcon,
  UserPlusIcon,
  Loader2Icon,
  FileIcon,
  UserCircleIcon,
  UsersIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  TagIcon,
  PhoneIcon,
  ListTodoIcon,
  ScissorsIcon,
  ShareIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { sanitizeEmailHtml } from '../lib/emailHtml'
import { sendSms, normalizePhoneDigits, formatPhoneForDisplay } from '../lib/sendSms'
import {
  applyConversationAction,
  setConversationState,
  STATE_AFTER_ACTION,
  ConversationAction,
  ConversationState,
} from '../api/conversationActions'
import {
  ConversationLabel,
  LABEL_COLORS,
  addLabelToConversation,
  createConversationLabel,
  fetchConversationLabels,
  fetchLabelAssignments,
  removeLabelFromConversation,
} from '../api/conversationLabels'
import { ConversationActionsMenu } from '../components/ConversationActionsMenu'
import { CustomerOtherConversations } from '../components/CustomerOtherConversations'
import { LabelPickerPopover } from '../components/LabelPickerPopover'
import { groupConversationsByCustomer } from '../lib/conversationGrouping'
import { groupAssignmentsByConversation } from '../lib/labelAssignments'
import {
  DEFAULT_INBOX_SORT,
  INBOX_SORT_OPTIONS,
  InboxSortKey,
  isInboxSortKey,
  sortConversations,
} from '../lib/inboxSort'
import { usePermissions } from '../hooks/usePermissions'
import { fetchUnifiedInboxFeed, inboxItemKey, parseInboxItemKey, type ConversationRow } from '../api/inboxFeed'
import { updatePhoneCallLog } from '../api/phoneCallLogs'
import { assignInboxItem, bulkAssignInboxItems, fetchInboxAssignees, setInboxHandlingStatus, suggestAssigneeForCustomer } from '../api/inboxAssignment'
import { fetchInboxLabelAssignments, groupInboxLabelsByEntity, addInboxLabel, removeInboxLabel, setPhoneCallLogState } from '../api/inboxLabels'
import { fetchInboxQueues } from '../api/inboxQueues'
import { countOpenTasksForSource, createInboxTask } from '../api/inboxTasks'
import { countInboxItemNotes } from '../api/inboxNotes'
import { moveMessageToConversation } from '../api/inboxSplit'
import type { HandlingStatus, InboxAssignee, InboxFeedItem, InboxItemType, InboxQueue, InboxViewFilter } from '../types/inbox'
import { canAssignInboxToOthers, isEntityUnlinked, assigneeDisplayName } from '../lib/inboxPermissions'
import { INBOX_VIEW_FILTERS, matchesInboxViewFilter } from '../lib/inboxFilters'
import { getErrorMessage } from '../lib/errors'
import { AssigneePicker } from '../components/inbox/AssigneePicker'
import { HandlingStatusSelect } from '../components/inbox/HandlingStatusSelect'
import { UnifiedInboxListItem } from '../components/inbox/UnifiedInboxListItem'
import { InboxListItemActionsMenu } from '../components/inbox/InboxListItemActionsMenu'
import { InboxTasksTab } from '../components/inbox/InboxTasksTab'
import { InboxCallDetailPanel } from '../components/inbox/InboxCallDetailPanel'
import { SplitConversationModal } from '../components/inbox/SplitConversationModal'
import { ShareMessagesModal } from '../components/inbox/ShareMessagesModal'
import { InboxSuggestBanner } from '../components/inbox/InboxSuggestBanner'
import { InboxConversationDetailHeader } from '../components/inbox/InboxConversationDetailHeader'
import { InboxUnlinkedLinkModal } from '../components/inbox/InboxUnlinkedLinkModal'
import { InboxDetailSidebar } from '../components/inbox/InboxDetailSidebar'
import { InboxEntityLinkPopover } from '../components/inbox/InboxEntityLinkPopover'
import {
  INBOX_ENTITY_LINK_FIELDS,
  fetchInboxEntityLinkLabels,
  searchInboxEntityLinks,
  type InboxEntityLinkKey,
} from '../lib/inboxEntityLinks'

function inboxAuthorName(profile: { first_name?: string | null; last_name?: string | null; email?: string } | null | undefined): string | null {
  if (!profile) return null
  const name = [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim()
  return name || profile.email || null
}

interface Conversation {
  id: string
  channel: 'whatsapp' | 'sms' | 'email'
  contact_name: string
  contact_info: string
  last_message: string | null
  last_message_at: string | null
  unread_count: number
  customer_id: string | null
  property_id?: string | null
  contract_id?: string | null
  job_id?: string | null
  employee_id?: string | null
  lead_id?: string | null
  proposal_id?: string | null
  mailbox_id?: string | null
  received_address?: string | null
  state?: ConversationState | null
  subject?: string | null
  assigned_to_user_id?: string | null
  assigned_at?: string | null
  handling_status?: HandlingStatus | null
  waiting_until?: string | null
  priority?: string | null
  queue_id?: string | null
  sla_due_at?: string | null
  internal_notes?: string | null
  created_at: string
}

/** Conversations without a state yet (pre-migration rows) count as active. */
function convoState(c: Conversation): ConversationState {
  return c.state || 'active'
}

const STATE_VIEWS: Array<{ value: ConversationState; label: string }> = [
  { value: 'active', label: 'Inbox' },
  { value: 'archived', label: 'Archived' },
  { value: 'spam', label: 'Spam' },
  { value: 'deleted', label: 'Trash' },
]

const STATE_BADGE: Partial<Record<ConversationState, { label: string; className: string }>> = {
  archived: { label: 'Archived', className: 'bg-gray-100 text-gray-600' },
  spam: { label: 'Spam', className: 'bg-red-50 text-red-600' },
  deleted: { label: 'In Trash', className: 'bg-gray-200 text-gray-600' },
}

/** Mailbox metadata (RLS hides other users' personal mailboxes; credentials never reach the client). */
interface Mailbox {
  id: string
  email_address: string
  display_name: string
  mailbox_type: 'shared' | 'personal'
  owner_user_id: string | null
  aliases: string[]
}

/** A selectable From address: a mailbox's own address or one of its aliases. */
interface FromOption {
  address: string
  mailboxId: string
  personal: boolean
  label: string
}

function buildFromOptions(mailboxes: Mailbox[]): FromOption[] {
  const options: FromOption[] = []
  for (const mb of mailboxes) {
    const personal = mb.mailbox_type === 'personal'
    options.push({
      address: mb.email_address,
      mailboxId: mb.id,
      personal,
      label: mb.display_name ? `${mb.display_name} <${mb.email_address}>` : mb.email_address,
    })
    if (!personal) {
      for (const alias of mb.aliases || []) {
        options.push({ address: alias, mailboxId: mb.id, personal: false, label: alias })
      }
    }
  }
  return options
}

async function emailAuthHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  return token ? { Authorization: `Bearer ${token}` } : {}
}

interface Message {
  id: string
  conversation_id: string
  channel: 'whatsapp' | 'sms' | 'email'
  direction: 'inbound' | 'outbound'
  contact_name: string | null
  contact_phone: string | null
  contact_email: string | null
  subject: string | null
  body: string
  body_html?: string | null
  status: string
  customer_id: string | null
  created_at: string
  sent_by?: string | null
  sent_by_name?: string | null
}

interface MessageAttachment {
  id: string
  message_id: string
  file_name: string
  file_path: string
  mime_type: string | null
  file_size: number | null
  content_id?: string | null
  is_inline?: boolean
}

interface Customer {
  id: string
  company_name: string
  email?: string
  phone?: string
}

type ChannelFilter = 'all' | 'whatsapp' | 'sms' | 'email' | 'call'
type InboxMode = 'unified' | 'tasks'

const CHANNEL_META: Record<
  'whatsapp' | 'sms' | 'email',
  { label: string; color: string; bg: string; border: string; icon: React.ElementType }
> = {
  whatsapp: {
    label: 'WhatsApp',
    color: 'text-green-600',
    bg: 'bg-green-50',
    border: 'border-green-200',
    icon: MessageSquareIcon,
  },
  sms: {
    label: 'SMS',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: SmartphoneIcon,
  },
  email: {
    label: 'Email',
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    icon: MailIcon,
  },
}

// Entity link fields wired up in the 20260720_inbox_entity_links migration.
// Used for the link picker, linked chips, and Inbox filtering.
const ENTITY_LINK_FIELDS = INBOX_ENTITY_LINK_FIELDS

function isUnlinked(c: Conversation) {
  return isEntityUnlinked(c)
}

function conversationToFeedItem(c: Conversation): InboxFeedItem {
  return {
    itemType: 'conversation',
    id: c.id,
    sortAt: c.last_message_at || c.created_at,
    channel: c.channel,
    contactName: c.contact_name || c.contact_info || 'Unknown',
    contactInfo: c.contact_info,
    preview: c.last_message || 'No messages yet',
    subject: c.subject,
    unreadCount: c.unread_count || 0,
    state: c.state || 'active',
    handlingStatus: c.handling_status || 'open',
    waitingUntil: c.waiting_until || null,
    assignedToUserId: c.assigned_to_user_id || null,
    assignedAt: c.assigned_at || null,
    priority: (c.priority as InboxFeedItem['priority']) || 'normal',
    queueId: c.queue_id || null,
    slaDueAt: c.sla_due_at || null,
    customerId: c.customer_id,
    propertyId: c.property_id || null,
    contractId: c.contract_id || null,
    jobId: c.job_id || null,
    leadId: c.lead_id || null,
    proposalId: c.proposal_id || null,
    employeeId: c.employee_id || null,
    mailboxId: c.mailbox_id || null,
    receivedAddress: c.received_address || null,
    staffNotes: c.internal_notes?.trim() || null,
  }
}

function statusIcon(status: string) {
  switch (status) {
    case 'sent':
      return <CheckIcon className="w-3.5 h-3.5 text-gray-400" />
    case 'delivered':
      return <CheckCheckIcon className="w-3.5 h-3.5 text-gray-400" />
    case 'read':
      return <CheckCheckIcon className="w-3.5 h-3.5 text-blue-500" />
    case 'failed':
      return <AlertCircleIcon className="w-3.5 h-3.5 text-red-500" />
    default:
      return null
  }
}

function formatTime(dateStr: string | null) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return 'now'
  if (diffMin < 60) return `${diffMin}m`
  if (diffHr < 24) return `${diffHr}h`
  if (diffDay < 7) return d.toLocaleDateString(undefined, { weekday: 'short' })
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function formatFullTime(dateStr: string) {
  const d = new Date(dateStr)
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export interface InboxProps {
  /** When true, hide standalone page chrome and use /messages deep-links. */
  embedded?: boolean
  /** Conversation id from Messages hub route/query (overrides /inbox/:id). */
  hubConversationId?: string | null
  /** Optional leading chrome (e.g. Messages External/Internal tabs) merged into the filter bar. */
  embeddedLeading?: React.ReactNode
}

export function Inbox({
  embedded = false,
  hubConversationId = null,
  embeddedLeading = null,
}: InboxProps = {}) {
  const navigate = useNavigate()
  const { id: routeConvoId } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const entityFilterType = searchParams.get('entityType')
  const entityFilterId = searchParams.get('entityId')
  const deepLinkConvoId = hubConversationId || searchParams.get('id') || routeConvoId || null
  const { profile, isAdmin } = usePermissions()
  const currentUserId = profile?.id || null
  const canAssignOthers = canAssignInboxToOthers(profile?.role) || isAdmin

  function messagesHubPath(convoId?: string | null) {
    if (convoId) return `/messages/${convoId}?tab=external`
    return '/messages?tab=external'
  }

  function outboundSenderFields() {
    return {
      sent_by: currentUserId,
      sent_by_name: inboxAuthorName(profile),
    }
  }

  const [feedItems, setFeedItems] = useState<InboxFeedItem[]>([])
  const [assignees, setAssignees] = useState<InboxAssignee[]>([])
  const [inboxQueues, setInboxQueues] = useState<InboxQueue[]>([])
  const [inboxLabelIdsByEntity, setInboxLabelIdsByEntity] = useState<Record<string, string[]>>({})
  const [viewFilter, setViewFilter] = useState<InboxViewFilter>(
    () => (localStorage.getItem('inbox.viewFilter') as InboxViewFilter) || 'all_open'
  )
  const [assigneeFilterId, setAssigneeFilterId] = useState('')
  const [queueFilterId, setQueueFilterId] = useState('')
  const [selectedFeedKey, setSelectedFeedKey] = useState<string | null>(null)
  const [selectedCallItem, setSelectedCallItem] = useState<InboxFeedItem | null>(null)
  const [callHasNotes, setCallHasNotes] = useState(false)
  const [splitModal, setSplitModal] = useState<{ messageId: string; preview: string } | null>(null)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [selectedMessageIds, setSelectedMessageIds] = useState<string[]>([])
  const [taskSuggest, setTaskSuggest] = useState<{ title: string; description: string; actionType?: string } | null>(null)
  const [resolveTaskWarning, setResolveTaskWarning] = useState<number | null>(null)
  const [pendingHandling, setPendingHandling] = useState<{
    status: HandlingStatus
    waitingUntil: string | null
  } | null>(null)
  const [suggestAssignee, setSuggestAssignee] = useState<{ userId: string; name: string } | null>(null)
  const [dismissedSuggests, setDismissedSuggests] = useState<Record<string, boolean>>({})
  const [movingMessageId, setMovingMessageId] = useState<string | null>(null)
  const [bulkMode, setBulkMode] = useState(false)
  const [bulkSelectedKeys, setBulkSelectedKeys] = useState<string[]>([])
  const [bulkLabelId, setBulkLabelId] = useState('')

  const [conversations, setConversations] = useState<Conversation[]>([])
  const [messages, setMessages] = useState<Message[]>([])
  const [attachmentsByMessage, setAttachmentsByMessage] = useState<Record<string, MessageAttachment[]>>({})
  // attachment id -> short-lived signed URL for rendering email images inline
  const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>({})
  const [customers, setCustomers] = useState<Customer[]>([])
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([])
  const [emailAddressFilter, setEmailAddressFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [selectedConvo, setSelectedConvo] = useState<Conversation | null>(null)
  const [inboxMode, setInboxMode] = useState<InboxMode>('unified')
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all')
  const [stateFilter, setStateFilter] = useState<ConversationState>('active')
  const [unlinkedOnly, setUnlinkedOnly] = useState(false)
  const [staffUnassignedOnly, setStaffUnassignedOnly] = useState(false)
  // "Group by customer" list view — presentation only, threads stay separate
  const [groupByCustomer, setGroupByCustomer] = useState(
    () => localStorage.getItem('inbox.groupByCustomer') === '1'
  )
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
  // Labels (Phase 8.10): tenant label catalog + per-conversation assignments
  const [inboxLabels, setInboxLabels] = useState<ConversationLabel[]>([])
  const [labelIdsByConvo, setLabelIdsByConvo] = useState<Record<string, string[]>>({})
  const [labelFilter, setLabelFilter] = useState(
    () => localStorage.getItem('inbox.labelFilter') || ''
  )
  const [sortBy, setSortBy] = useState<InboxSortKey>(() => {
    const saved = localStorage.getItem('inbox.sortBy') || ''
    return isInboxSortKey(saved) ? saved : DEFAULT_INBOX_SORT
  })
  // Which conversation has the label picker open, and from where it was opened
  const [labelPickerFor, setLabelPickerFor] = useState<{
    itemType: import('../types/inbox').InboxItemType
    itemId: string
    anchor: 'row' | 'header'
  } | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [composeOpen, setComposeOpen] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [sendingReply, setSendingReply] = useState(false)
  const [uploadingFile, setUploadingFile] = useState(false)
  const [mobileShowMessages, setMobileShowMessages] = useState(false)

  const [composeChannel, setComposeChannel] = useState<'whatsapp' | 'sms' | 'email'>('email')
  const [composeRecipient, setComposeRecipient] = useState('')
  const [composeSubject, setComposeSubject] = useState('')
  const [composeBody, setComposeBody] = useState('')
  const [composeCustomerId, setComposeCustomerId] = useState('')
  const [composeSending, setComposeSending] = useState(false)
  const [composeFrom, setComposeFrom] = useState('')
  const [smsFromNumbers, setSmsFromNumbers] = useState<string[]>([])
  const [composeEmailFrom, setComposeEmailFrom] = useState('')

  const [linkPickerOpen, setLinkPickerOpen] = useState(false)
  const [linkTargetItem, setLinkTargetItem] = useState<InboxFeedItem | null>(null)
  const [linkPickerDefaultKey, setLinkPickerDefaultKey] = useState<InboxEntityLinkKey>('customer_id')
  const [linkedLabels, setLinkedLabels] = useState<Record<string, string>>({})

  const [assigning, setAssigning] = useState(false)
  const [unlinkedModalOpen, setUnlinkedModalOpen] = useState(false)
  const [syncingEmail, setSyncingEmail] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const selectedConvoIdRef = useRef<string | null>(null)

  useEffect(() => {
    selectedConvoIdRef.current = selectedConvo?.id || null
  }, [selectedConvo?.id])

  // Live updates: the server auto-syncs mailboxes on an interval; new inbound
  // rows arrive here via Supabase realtime (RLS-filtered), same pattern as
  // the call screen-pop. No page reload or manual sync needed.
  useEffect(() => {
    const channel = supabase
      .channel('inbox-messages-live')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        (payload) => {
          const row = payload.new as { direction?: string; conversation_id?: string }
          if (row?.direction !== 'inbound') return
          fetchConversations(true)
          if (row.conversation_id && row.conversation_id === selectedConvoIdRef.current) {
            fetchMessages(row.conversation_id)
          }
        }
      )
      .subscribe()
    return () => {
      supabase.removeChannel(channel)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchConversations()
    refreshUnifiedFeed()
    fetchCustomers()
    fetchMailboxes()
    fetchInboxLabels()
    fetchInboxAssignees()
      .then(setAssignees)
      .catch(() => setAssignees([]))
    fetchInboxQueues()
      .then(setInboxQueues)
      .catch(() => setInboxQueues([]))
    // Business SMS from-numbers for the compose selector (server config, no key exposure)
    fetch('/api/config/client')
      .then((r) => r.json())
      .then((cfg) => {
        setSmsFromNumbers(cfg.smsFromNumbers || [])
        setComposeFrom(cfg.smsDefaultFrom || cfg.smsFromNumbers?.[0] || '')
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (selectedConvo) {
      fetchMessages(selectedConvo.id)
      fetchLinkedLabels(selectedConvo)
    }
  }, [selectedConvo?.id])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Deep-link support: /inbox/:id or /messages?id= /messages/:id opens that conversation.
  useEffect(() => {
    if (deepLinkConvoId && conversations.length > 0) {
      const found = conversations.find((c) => c.id === deepLinkConvoId)
      if (found && found.id !== selectedConvo?.id) selectConversation(found)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkConvoId, conversations])

  async function refreshUnifiedFeed() {
    try {
      const items = await fetchUnifiedInboxFeed()
      setFeedItems(items)
    } catch {
      setFeedItems(conversations.map(conversationToFeedItem))
    }
  }

  async function fetchConversations(quiet = false) {
    try {
      if (!quiet) setLoading(true)
      const { data, error } = await supabase
        .from('conversations')
        .select('*')
        .order('last_message_at', { ascending: false })

      if (error) throw error
      setConversations(data || [])
      if (!quiet) await refreshUnifiedFeed()
    } catch {
      if (!quiet) setConversations([])
    } finally {
      if (!quiet) setLoading(false)
    }
  }

  async function fetchInboxLabels() {
    try {
      const [labelList, assignments, polyAssignments] = await Promise.all([
        fetchConversationLabels(),
        fetchLabelAssignments(),
        fetchInboxLabelAssignments().catch(() => []),
      ])
      setInboxLabels(labelList)
      setLabelIdsByConvo(groupAssignmentsByConversation(assignments))
      setInboxLabelIdsByEntity(groupInboxLabelsByEntity(polyAssignments))
      // Saved filter may point at a label that was deleted since
      setLabelFilter((prev) => {
        if (prev && !labelList.some((l) => l.id === prev)) {
          localStorage.setItem('inbox.labelFilter', '')
          return ''
        }
        return prev
      })
    } catch {
      // Labels are non-critical; the inbox still works without them
    }
  }

  function changeLabelFilter(value: string) {
    setLabelFilter(value)
    localStorage.setItem('inbox.labelFilter', value)
  }

  function changeSortBy(value: InboxSortKey) {
    setSortBy(value)
    localStorage.setItem('inbox.sortBy', value)
  }

  /** Add/remove one label on a conversation (optimistic, reverts on failure). */
  async function toggleConversationLabel(
    convoId: string,
    label: ConversationLabel,
    assigned: boolean
  ) {
    const prev = labelIdsByConvo
    setLabelIdsByConvo((p) => ({
      ...p,
      [convoId]: assigned
        ? (p[convoId] || []).filter((id) => id !== label.id)
        : [...(p[convoId] || []), label.id],
    }))
    try {
      if (assigned) await removeLabelFromConversation(convoId, label.id)
      else await addLabelToConversation(convoId, label.id)
    } catch (err: unknown) {
      setLabelIdsByConvo(prev)
      alert(getErrorMessage(err, 'Failed to update label'))
    }
  }

  /** Inline creation from the picker: create the label, then apply it. */
  async function createAndAssignLabel(convoId: string, name: string) {
    try {
      const color = LABEL_COLORS[inboxLabels.length % LABEL_COLORS.length]
      const label = await createConversationLabel(name, color)
      setInboxLabels((prevLabels) =>
        [...prevLabels, label].sort(
          (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)
        )
      )
      await addLabelToConversation(convoId, label.id)
      setLabelIdsByConvo((p) => ({ ...p, [convoId]: [...(p[convoId] || []), label.id] }))
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Failed to create label'))
    }
  }

  async function fetchMessages(conversationId: string) {
    try {
      setMessagesLoading(true)
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true })

      if (error) throw error
      setMessages(data || [])

      const ids = (data || []).map((m) => m.id)
      if (ids.length > 0) {
        const { data: attachments } = await supabase
          .from('message_attachments')
          .select('*')
          .in('message_id', ids)
        const grouped: Record<string, MessageAttachment[]> = {}
        for (const att of attachments || []) {
          grouped[att.message_id] = [...(grouped[att.message_id] || []), att]
        }
        setAttachmentsByMessage(grouped)

        // Signed URLs for image attachments so photos render inline
        const imageAtts = (attachments || []).filter((a) => (a.mime_type || '').startsWith('image/'))
        if (imageAtts.length > 0) {
          const { data: signed } = await supabase.storage
            .from('message-attachments')
            .createSignedUrls(imageAtts.map((a) => a.file_path), 3600)
          const urls: Record<string, string> = {}
          imageAtts.forEach((att, i) => {
            const url = signed?.[i]?.signedUrl
            if (url) urls[att.id] = url
          })
          setAttachmentUrls(urls)
        } else {
          setAttachmentUrls({})
        }
      } else {
        setAttachmentsByMessage({})
        setAttachmentUrls({})
      }

      await supabase
        .from('conversations')
        .update({ unread_count: 0 })
        .eq('id', conversationId)

      setConversations((prev) =>
        prev.map((c) => (c.id === conversationId ? { ...c, unread_count: 0 } : c))
      )
    } catch {
      setMessages([])
      setAttachmentsByMessage({})
    } finally {
      setMessagesLoading(false)
    }
  }

  async function fetchLinkedLabels(convo: Conversation) {
    setLinkedLabels(
      await fetchInboxEntityLinkLabels({
        customer_id: convo.customer_id,
        lead_id: convo.lead_id,
        property_id: convo.property_id,
        contract_id: convo.contract_id,
        job_id: convo.job_id,
        employee_id: convo.employee_id,
        proposal_id: convo.proposal_id,
      })
    )
  }

  async function fetchCustomers() {
    try {
      const { data } = await supabase.from('customers').select('id, company_name, email, phone').order('company_name')
      setCustomers(data || [])
    } catch {
      setCustomers([])
    }
  }

  async function fetchMailboxes() {
    try {
      const { data } = await supabase
        .from('mailboxes')
        .select('id, email_address, display_name, mailbox_type, owner_user_id, aliases')
        .eq('is_active', true)
        .order('created_at', { ascending: true })
      setMailboxes(data || [])
      const firstShared = (data || []).find((m) => m.mailbox_type === 'shared') || (data || [])[0]
      if (firstShared) setComposeEmailFrom((prev) => prev || firstShared.email_address)
    } catch {
      setMailboxes([])
    }
  }

  async function handleSendReply() {
    if (!replyText.trim() || !selectedConvo) return
    setSendingReply(true)
    try {
      const newMsg = {
        conversation_id: selectedConvo.id,
        channel: selectedConvo.channel,
        direction: 'outbound' as const,
        contact_name: selectedConvo.contact_name,
        contact_phone: selectedConvo.channel !== 'email' ? selectedConvo.contact_info : null,
        contact_email: selectedConvo.channel === 'email' ? selectedConvo.contact_info : null,
        body: replyText.trim(),
        status: 'sent',
        customer_id: selectedConvo.customer_id,
        ...outboundSenderFields(),
      }

      const { data, error } = await supabase.from('messages').insert(newMsg).select().single()
      if (error) throw error

      setMessages((prev) => [...prev, data])
      setReplyText('')

      if (selectedConvo.channel === 'email') {
        try {
          // Reply from the mailbox (or alias) this conversation belongs to
          const convoMailbox = mailboxes.find((m) => m.id === selectedConvo.mailbox_id)
          const replyFrom = selectedConvo.received_address || convoMailbox?.email_address || undefined
          const resp = await fetch('/api/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(await emailAuthHeaders()) },
            body: JSON.stringify({
              to: selectedConvo.contact_info,
              body: newMsg.body,
              from: replyFrom,
              // Server builds "Re: ..." + In-Reply-To/References from this
              // conversation's stored Message-IDs so the reply threads at the
              // recipient, and their reply threads back here.
              conversation_id: selectedConvo.id,
            }),
          })
          if (!resp.ok) {
            const info = await resp.json().catch(() => ({}))
            await supabase.from('messages').update({ status: 'failed' }).eq('id', data.id)
            setMessages((prev) =>
              prev.map((m) => (m.id === data.id ? { ...m, status: 'failed' } : m))
            )
            console.warn('Email send did not complete:', info.error || resp.statusText)
          } else {
            const info = await resp.json().catch(() => ({} as any))
            if (info.messageId) {
              // Keep the sent Message-ID so the counterparty's reply threads back
              const patch: Record<string, any> = { external_id: info.messageId }
              if (info.subject) patch.subject = info.subject
              await supabase.from('messages').update(patch).eq('id', data.id)
            }
          }
        } catch (sendErr) {
          await supabase.from('messages').update({ status: 'failed' }).eq('id', data.id)
          setMessages((prev) =>
            prev.map((m) => (m.id === data.id ? { ...m, status: 'failed' } : m))
          )
          console.warn('Email send request failed:', sendErr)
        }
      } else if (selectedConvo.channel === 'sms') {
        try {
          await sendSms({
            to: selectedConvo.contact_info,
            body: newMsg.body,
            from: composeFrom || undefined,
          })
        } catch (sendErr: any) {
          await supabase.from('messages').update({ status: 'failed' }).eq('id', data.id)
          setMessages((prev) =>
            prev.map((m) => (m.id === data.id ? { ...m, status: 'failed' } : m))
          )
          alert('SMS could not be sent: ' + (sendErr.message || 'Unknown error'))
        }
      }

      await supabase
        .from('conversations')
        .update({
          last_message: replyText.trim().slice(0, 200),
          last_message_at: new Date().toISOString(),
        })
        .eq('id', selectedConvo.id)

      setConversations((prev) =>
        prev.map((c) =>
          c.id === selectedConvo.id
            ? { ...c, last_message: replyText.trim().slice(0, 200), last_message_at: new Date().toISOString() }
            : c
        )
      )
    } catch (err) {
      console.error('Failed to send reply:', err)
    } finally {
      setSendingReply(false)
    }
  }

  async function handleAttachFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !selectedConvo) return
    setUploadingFile(true)
    try {
      const path = `${selectedConvo.id}/${Date.now()}_${file.name}`
      const { error: uploadErr } = await supabase.storage.from('message-attachments').upload(path, file)
      if (uploadErr) throw uploadErr

      const { data: newMsg, error: msgErr } = await supabase
        .from('messages')
        .insert({
          conversation_id: selectedConvo.id,
          channel: selectedConvo.channel,
          direction: 'outbound',
          contact_name: selectedConvo.contact_name,
          contact_phone: selectedConvo.channel !== 'email' ? selectedConvo.contact_info : null,
          contact_email: selectedConvo.channel === 'email' ? selectedConvo.contact_info : null,
          body: `📎 ${file.name}`,
          status: 'sent',
          customer_id: selectedConvo.customer_id,
          ...outboundSenderFields(),
        })
        .select()
        .single()
      if (msgErr) throw msgErr

      const { data: attachment, error: attErr } = await supabase
        .from('message_attachments')
        .insert({
          message_id: newMsg.id,
          file_name: file.name,
          file_path: path,
          mime_type: file.type || null,
          file_size: file.size || null,
        })
        .select()
        .single()
      if (attErr) throw attErr

      setMessages((prev) => [...prev, newMsg])
      setAttachmentsByMessage((prev) => ({ ...prev, [newMsg.id]: [attachment] }))

      await supabase
        .from('conversations')
        .update({ last_message: `📎 ${file.name}`, last_message_at: new Date().toISOString() })
        .eq('id', selectedConvo.id)
    } catch (err) {
      console.error('Failed to attach file:', err)
      alert('Failed to upload attachment')
    } finally {
      setUploadingFile(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  async function handleDownloadAttachment(attachment: MessageAttachment) {
    try {
      const { data, error } = await supabase.storage
        .from('message-attachments')
        .createSignedUrl(attachment.file_path, 300)
      if (error) throw error
      if (data?.signedUrl) window.open(data.signedUrl, '_blank')
    } catch (err) {
      console.error('Failed to open attachment:', err)
    }
  }

  async function handleComposeSend() {
    if (!composeRecipient.trim() || !composeBody.trim()) return
    setComposeSending(true)
    try {
      // SMS threads store the normalized number so inbound webhook replies match the thread
      const recipient =
        composeChannel === 'sms'
          ? normalizePhoneDigits(composeRecipient.trim()) || composeRecipient.trim()
          : composeRecipient.trim()

      const fromOption =
        composeChannel === 'email'
          ? buildFromOptions(mailboxes).find((o) => o.address === composeEmailFrom)
          : undefined

      const { data: convo, error: convoErr } = await supabase
        .from('conversations')
        .insert({
          channel: composeChannel,
          contact_name: composeRecipient.trim(),
          contact_info: recipient,
          subject: composeChannel === 'email' ? composeSubject.trim() || null : null,
          last_message: composeBody.trim().slice(0, 200),
          last_message_at: new Date().toISOString(),
          unread_count: 0,
          customer_id: composeCustomerId || null,
          ...(fromOption ? { mailbox_id: fromOption.mailboxId, received_address: fromOption.address } : {}),
        })
        .select()
        .single()

      if (convoErr) throw convoErr

      const msgPayload = {
        conversation_id: convo.id,
        channel: composeChannel,
        direction: 'outbound' as const,
        contact_name: composeRecipient.trim(),
        contact_phone: composeChannel !== 'email' ? recipient : null,
        contact_email: composeChannel === 'email' ? recipient : null,
        subject: composeChannel === 'email' ? composeSubject.trim() || null : null,
        body: composeBody.trim(),
        status: 'sent',
        customer_id: composeCustomerId || null,
        ...outboundSenderFields(),
      }

      const { data: sentMsg } = await supabase.from('messages').insert(msgPayload).select('id').single()

      if (composeChannel === 'email') {
        try {
          const resp = await fetch('/api/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(await emailAuthHeaders()) },
            body: JSON.stringify({
              to: composeRecipient.trim(),
              subject: composeSubject.trim(),
              body: composeBody.trim(),
              from: fromOption?.address || undefined,
            }),
          })
          if (!resp.ok) {
            const info = await resp.json().catch(() => ({}))
            await supabase.from('messages').update({ status: 'failed' }).eq('conversation_id', convo.id)
            throw new Error(info.error || 'Email send failed')
          }
          // Store the sent Message-ID so the recipient's reply threads into this conversation
          const info = await resp.json().catch(() => ({} as any))
          if (info.messageId && sentMsg?.id) {
            await supabase.from('messages').update({ external_id: info.messageId }).eq('id', sentMsg.id)
          }
        } catch (sendErr: any) {
          await supabase.from('messages').update({ status: 'failed' }).eq('conversation_id', convo.id)
          alert('Message saved but email failed: ' + (sendErr.message || 'Unknown error'))
        }
      } else if (composeChannel === 'sms') {
        try {
          await sendSms({ to: recipient, body: composeBody.trim(), from: composeFrom || undefined })
        } catch (sendErr: any) {
          await supabase.from('messages').update({ status: 'failed' }).eq('conversation_id', convo.id)
          alert('Message saved but SMS failed: ' + (sendErr.message || 'Unknown error'))
        }
      }

      setConversations((prev) => [convo, ...prev])
      setSelectedConvo(convo)
      resetCompose()
      fetchMessages(convo.id)
    } catch (err) {
      console.error('Failed to send message:', err)
    } finally {
      setComposeSending(false)
    }
  }

  function resetCompose() {
    setComposeOpen(false)
    setComposeChannel('email')
    setComposeRecipient('')
    setComposeSubject('')
    setComposeBody('')
    setComposeCustomerId('')
    const firstShared = mailboxes.find((m) => m.mailbox_type === 'shared') || mailboxes[0]
    setComposeEmailFrom(firstShared?.email_address || '')
  }

  async function handleSyncEmail() {
    setSyncingEmail(true)
    try {
      const resp = await fetch('/api/email/poll-inbox', { method: 'POST' })
      const info = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        alert(info.error || 'Email sync failed. Check IMAP_* env settings on the server.')
        return
      }
      await fetchConversations()
      alert(
        info.imported != null
          ? `Email sync complete. Imported ${info.imported} message(s).`
          : 'Email sync complete.'
      )
    } catch (err: any) {
      alert('Email sync failed: ' + (err.message || 'Unknown error'))
    } finally {
      setSyncingEmail(false)
    }
  }

  function selectConversation(convo: Conversation) {
    setSelectedConvo(convo)
    setSelectedCallItem(null)
    setSelectedFeedKey(inboxItemKey('conversation', convo.id))
    setMobileShowMessages(true)
    setLinkPickerOpen(false)
    setSelectedMessageIds([])
    setShareModalOpen(false)
    setSplitModal(null)
    setSuggestAssignee(null)
    const target = messagesHubPath(convo.id)
    const current =
      window.location.pathname + (window.location.search || '')
    if (current !== target && !current.startsWith(`/messages/${convo.id}`)) {
      navigate(target, { replace: true })
    }
  }

    function dismissSuggest(key: string) {
    setDismissedSuggests((prev) => ({ ...prev, [key]: true }))
    setTaskSuggest(null)
    setSuggestAssignee(null)
  }

  function toggleBulkSelect(item: InboxFeedItem) {
    const key = inboxItemKey(item.itemType, item.id)
    setBulkSelectedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))
  }

  async function runBulkAssign(userId: string | null) {
    const items = bulkSelectedKeys.flatMap((key) => {
      const parsed = parseInboxItemKey(key)
      return parsed ? [{ itemType: parsed.itemType, itemId: parsed.id }] : []
    })
    if (!items.length) return
    await bulkAssignInboxItems(items, userId)
    setBulkSelectedKeys([])
    setBulkMode(false)
    await refreshUnifiedFeed()
  }

  async function runBulkLabel() {
    if (!bulkLabelId) return
    for (const key of bulkSelectedKeys) {
      const parsed = parseInboxItemKey(key)
      if (!parsed) continue
      await addInboxLabel(parsed.itemType, parsed.id, bulkLabelId)
    }
    setBulkSelectedKeys([])
    setBulkMode(false)
    setBulkLabelId('')
    await fetchInboxLabels()
    await refreshUnifiedFeed()
  }

  async function runBulkArchive() {
    for (const key of bulkSelectedKeys) {
      const parsed = parseInboxItemKey(key)
      if (!parsed) continue
      if (parsed.itemType === 'conversation') {
        await setConversationState(parsed.id, 'archived')
      } else {
        await setPhoneCallLogState(parsed.id, 'archived')
      }
    }
    setBulkSelectedKeys([])
    setBulkMode(false)
    await refreshUnifiedFeed()
  }

  async function runBulkResolve() {
    for (const key of bulkSelectedKeys) {
      const parsed = parseInboxItemKey(key)
      if (!parsed) continue
      await setInboxHandlingStatus({
        itemType: parsed.itemType,
        itemId: parsed.id,
        handlingStatus: 'resolved',
      })
    }
    setBulkSelectedKeys([])
    setBulkMode(false)
    await refreshUnifiedFeed()
  }

    async function maybeSuggestAssigneeForCustomer(customerId: string) {
    if (selectedConvo?.assigned_to_user_id) return
    const userId = await suggestAssigneeForCustomer(customerId)
    if (!userId) return
    const user = assignees.find((a) => a.id === userId)
    if (!user) return
    const key = `assignee:conversation:${selectedConvo?.id}`
    if (dismissedSuggests[key]) return
    setSuggestAssignee({ userId, name: assigneeDisplayName(user) })
  }

  async function handleConvoAssign(userId: string | null) {
    if (!selectedConvo) return
    try {
      await assignInboxItem({ itemType: 'conversation', itemId: selectedConvo.id, assigneeUserId: userId })
      const updated: Conversation = {
        ...selectedConvo,
        assigned_to_user_id: userId,
        handling_status: userId ? 'assigned' : 'open',
      }
      setSelectedConvo(updated)
      setConversations((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
      await refreshUnifiedFeed()
      setSuggestAssignee(null)
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Assignment failed'))
    }
  }

  async function applyConvoHandling(status: HandlingStatus, waitingUntil?: string | null) {
    if (!selectedConvo) return
    try {
      await setInboxHandlingStatus({
        itemType: 'conversation',
        itemId: selectedConvo.id,
        handlingStatus: status,
        waitingUntil,
      })
      const updated: Conversation = {
        ...selectedConvo,
        handling_status: status,
        waiting_until: waitingUntil ?? null,
      }
      setSelectedConvo(updated)
      setConversations((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
      await refreshUnifiedFeed()
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Failed to update status'))
    }
  }

  async function handleConvoHandling(status: HandlingStatus, waitingUntil?: string | null) {
    if (!selectedConvo) return
    if (status === 'resolved') {
      const count = await countOpenTasksForSource('conversation', selectedConvo.id)
      if (count > 0) {
        setResolveTaskWarning(count)
        setPendingHandling({ status, waitingUntil: waitingUntil ?? null })
        return
      }
    }
    await applyConvoHandling(status, waitingUntil)
  }

  async function confirmResolveDespiteOpenTasks() {
    if (!pendingHandling) return
    await applyConvoHandling(pendingHandling.status, pendingHandling.waitingUntil)
    setResolveTaskWarning(null)
    setPendingHandling(null)
  }

  async function confirmTaskSuggest() {
    const source = selectedCallItem || (selectedConvo ? conversationToFeedItem(selectedConvo) : null)
    if (!source || !taskSuggest || !currentUserId) return
    const assignee = source.assignedToUserId || currentUserId
    try {
      await createInboxTask({
        title: taskSuggest.title,
        description: taskSuggest.description,
        assignee_id: assignee,
        action_type: taskSuggest.actionType || null,
        source_type: source.itemType,
        source_id: source.id,
        customer_id: source.customerId,
        property_id: source.propertyId,
        contract_id: source.contractId,
        job_id: source.jobId,
        lead_id: source.leadId,
        proposal_id: source.proposalId,
      })
      const key = selectedCallItem
        ? `task:call:${selectedCallItem.id}`
        : `task:conversation:${selectedConvo?.id}`
      dismissSuggest(key)
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Failed to create task'))
    }
  }

  function toggleMessageSelection(messageId: string) {
    setSelectedMessageIds((prev) =>
      prev.includes(messageId) ? prev.filter((id) => id !== messageId) : [...prev, messageId]
    )
  }

  async function handleMoveMessage(messageId: string, targetConversationId: string) {
    setMovingMessageId(messageId)
    try {
      await moveMessageToConversation(messageId, targetConversationId)
      if (selectedConvo) await fetchMessages(selectedConvo.id)
      await fetchConversations(true)
      await refreshUnifiedFeed()
    } catch (err: any) {
      alert(err.message || 'Failed to move message')
    } finally {
      setMovingMessageId(null)
    }
  }

  function openShareModal() {
    if (selectedMessageIds.length < 1) {
      alert('Select at least one message to share')
      return
    }
    if (selectedMessageIds.length > 5) {
      alert('You can share at most 5 messages per packet')
      return
    }
    setShareModalOpen(true)
  }

  // -- Archive / spam / delete lifecycle actions --
  async function handleConversationAction(convo: Conversation, action: ConversationAction) {
    try {
      const nextState = await applyConversationAction(convo, action)
      setConversations((prev) => prev.map((c) => (c.id === convo.id ? { ...c, state: nextState } : c)))
      if (selectedConvo?.id === convo.id) {
        if (nextState !== stateFilter) {
          // Conversation left the current view — go back to the list
          setSelectedConvo(null)
          setMobileShowMessages(false)
          navigate(messagesHubPath(), { replace: true })
        } else {
          setSelectedConvo((prev) => (prev ? { ...prev, state: nextState } : prev))
        }
      }
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Action failed. Please try again.'))
    }
  }

  // -- Link picker --
  async function linkConversationTo(entityKey: InboxEntityLinkKey, entityId: string, label: string) {
    const target =
      linkTargetItem ||
      (selectedConvo ? conversationToFeedItem(selectedConvo) : null) ||
      selectedCallItem
    if (!target) return
    try {
      if (target.itemType === 'conversation') {
        await supabase.from('conversations').update({ [entityKey]: entityId }).eq('id', target.id)
        const convo = conversations.find((c) => c.id === target.id) || selectedConvo
        if (convo) {
          const updated = { ...convo, [entityKey]: entityId }
          setSelectedConvo(updated)
          setConversations((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
        }
        if (entityKey === 'customer_id') await maybeSuggestAssigneeForCustomer(entityId)
      } else {
        await updatePhoneCallLog(target.id, { [entityKey]: entityId } as any)
        await refreshUnifiedFeed()
      }
      setLinkedLabels((prev) => ({ ...prev, [entityKey]: label }))
      setLinkPickerOpen(false)
      setLinkTargetItem(null)
    } catch (err) {
      console.error('Failed to link record:', err)
    }
  }

  async function unlinkConversation(key: string) {
    if (!selectedConvo) return
    try {
      await supabase.from('conversations').update({ [key]: null }).eq('id', selectedConvo.id)
      const updated = { ...selectedConvo, [key]: null }
      setSelectedConvo(updated)
      setConversations((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
      setLinkedLabels((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    } catch (err) {
      console.error('Failed to unlink conversation:', err)
    }
  }

  // -- Unassigned bucket: add as contact on existing customer/lead --
  async function searchUnlinkedRecords(type: 'customer' | 'lead', term: string) {
    if (!term.trim()) return []
    const results = await searchInboxEntityLinks(
      type === 'customer' ? 'customer_id' : 'lead_id',
      term
    )
    if (type === 'customer') {
      const ids = results.map((r) => r.id)
      if (!ids.length) return []
      const { data } = await supabase.from('customers').select('id, company_name, contacts').in('id', ids)
      return (data || []).map((r: any) => ({ id: r.id, name: r.company_name || 'Unnamed', contacts: r.contacts || [] }))
    }
    const { data } = await supabase.from('leads').select('id, company_name, contacts').in('id', results.map((r) => r.id))
    return (data || []).map((r: any) => ({ id: r.id, name: r.company_name || 'Unnamed', contacts: r.contacts || [] }))
  }

  /**
   * Link just this conversation to the customer/lead WITHOUT saving the sender
   * as a contact — for one-off emails (violation receipts, vendors, etc.).
   * The sender's next unrelated email will arrive unassigned again.
   */
  async function attachConversationOnly(record: { id: string }, type: 'customer' | 'lead' = 'customer') {
    if (!selectedConvo) return
    setAssigning(true)
    try {
      const linkField = type === 'customer' ? 'customer_id' : 'lead_id'
      await supabase.from('conversations').update({ [linkField]: record.id }).eq('id', selectedConvo.id)
      const updated = { ...selectedConvo, [linkField]: record.id }
      setSelectedConvo(updated)
      setConversations((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
            if (type === 'customer') await maybeSuggestAssigneeForCustomer(record.id)
      setUnlinkedModalOpen(false)
    } catch (err) {
      console.error('Failed to attach conversation:', err)
      alert('Failed to attach conversation')
    } finally {
      setAssigning(false)
    }
  }

  /** Saves the sender into the customer/lead contacts (future emails auto-link) AND links this conversation. */
  async function addAsNewContact(record: { id: string; contacts: any[] }, type: 'customer' | 'lead' = 'customer') {
    if (!selectedConvo) return
    setAssigning(true)
    try {
      const table = type === 'customer' ? 'customers' : 'leads'
      const newContact = {
        id: Date.now().toString() + Math.random().toString(36).slice(2),
        first_name: '',
        last_name: '',
        title: '',
        phone: selectedConvo.channel !== 'email' ? selectedConvo.contact_info : '',
        cell: '',
        email: selectedConvo.channel === 'email' ? selectedConvo.contact_info : '',
      }
      const nextContacts = [...(record.contacts || []), newContact]
      await supabase.from(table).update({ contacts: nextContacts }).eq('id', record.id)

      const linkField = type === 'customer' ? 'customer_id' : 'lead_id'
      await supabase.from('conversations').update({ [linkField]: record.id }).eq('id', selectedConvo.id)

      const updated = { ...selectedConvo, [linkField]: record.id }
      setSelectedConvo(updated)
      setConversations((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
      setUnlinkedModalOpen(false)

      const editRoute = type === 'customer' ? `/customers/edit/${record.id}?tab=contacts` : `/leads/edit/${record.id}?tab=contacts`
      navigate(editRoute)
    } catch (err) {
      console.error('Failed to add contact:', err)
      alert('Failed to add contact')
    } finally {
      setAssigning(false)
    }
  }

  const fromOptions = useMemo(() => buildFromOptions(mailboxes), [mailboxes])
  const mailboxById = useMemo(() => new Map(mailboxes.map((m) => [m.id, m])), [mailboxes])

  /** Address an email conversation belongs to: the To: alias if known, else its mailbox address. */
  function convoEmailAddress(c: Conversation): string {
    return (c.received_address || mailboxById.get(c.mailbox_id || '')?.email_address || '').toLowerCase()
  }

  const filteredConvos = useMemo(() => {
    const filtered = conversations.filter((c) => {
      if (convoState(c) !== stateFilter) return false
      if (channelFilter !== 'all' && c.channel !== channelFilter) return false
      if (unlinkedOnly && !isUnlinked(c)) return false
      if (channelFilter === 'email' && emailAddressFilter && convoEmailAddress(c) !== emailAddressFilter) {
        return false
      }
      if (labelFilter && !(labelIdsByConvo[c.id] || []).includes(labelFilter)) return false
      if (entityFilterType && entityFilterId) {
        const key = `${entityFilterType}_id`
        if ((c as any)[key] !== entityFilterId) return false
      }
      if (searchTerm) {
        const q = searchTerm.toLowerCase()
        return (
          c.contact_name?.toLowerCase().includes(q) ||
          c.contact_info?.toLowerCase().includes(q) ||
          c.last_message?.toLowerCase().includes(q)
        )
      }
      return true
    })
    return sortConversations(filtered, sortBy)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, stateFilter, channelFilter, unlinkedOnly, entityFilterType, entityFilterId, searchTerm, emailAddressFilter, mailboxById, labelFilter, labelIdsByConvo, sortBy])

  const assigneeById = useMemo(() => new Map(assignees.map((a) => [a.id, a])), [assignees])

  const filteredFeedItems = useMemo(() => {
    const base = feedItems.length > 0 ? feedItems : conversations.map(conversationToFeedItem)
    return base
      .filter((item) => {
        if (item.itemType === 'conversation' && convoState({ state: item.state } as Conversation) !== stateFilter) {
          return false
        }
        if (item.itemType === 'phone_call_log' && stateFilter !== 'active' && item.state !== stateFilter) {
          return false
        }
        if (item.itemType === 'phone_call_log' && stateFilter === 'active' && item.state !== 'active') {
          return false
        }
        if (channelFilter !== 'all') {
          if (channelFilter === 'call' && item.channel !== 'call') return false
          if (channelFilter !== 'call' && item.channel !== channelFilter) return false
        }
        if (unlinkedOnly && !isEntityUnlinked(item)) return false
        if (staffUnassignedOnly && (item.assignedToUserId || item.handlingStatus !== 'open')) return false
        if (viewFilter !== 'all_open' && !matchesInboxViewFilter(item, viewFilter, currentUserId, assigneeFilterId || null, queueFilterId || null)) {
          return false
        }
        const labelKey = inboxItemKey(item.itemType, item.id)
        if (labelFilter && !(inboxLabelIdsByEntity[labelKey] || labelIdsByConvo[item.id] || []).includes(labelFilter)) {
          return false
        }
        if (entityFilterType && entityFilterId) {
          const key = `${entityFilterType}Id` as keyof InboxFeedItem
          const snake = `${entityFilterType}_id` as keyof InboxFeedItem
          if ((item as any)[snake] !== entityFilterId && (item as any)[key] !== entityFilterId) return false
        }
        if (searchTerm) {
          const q = searchTerm.toLowerCase()
          return (
            item.contactName.toLowerCase().includes(q) ||
            item.contactInfo.toLowerCase().includes(q) ||
            item.preview.toLowerCase().includes(q)
          )
        }
        return true
      })
      .sort((a, b) => b.sortAt.localeCompare(a.sortAt))
  }, [
    feedItems,
    conversations,
    stateFilter,
    channelFilter,
    unlinkedOnly,
    staffUnassignedOnly,
    viewFilter,
    assigneeFilterId,
    queueFilterId,
    entityFilterType,
    entityFilterId,
    searchTerm,
    labelFilter,
    labelIdsByConvo,
    inboxLabelIdsByEntity,
    currentUserId,
  ])

  const staffUnassignedCount = useMemo(
    () => filteredFeedItems.filter((i) => !i.assignedToUserId && i.handlingStatus === 'open' && i.state === 'active').length,
    [filteredFeedItems]
  )


  function openLinkPickerForItem(item: InboxFeedItem) {
    selectFeedItem(item)
    setLinkTargetItem(item)
    setLinkPickerDefaultKey('customer_id')
    setLinkPickerOpen(true)
  }

  async function handleFeedItemResolve(item: InboxFeedItem) {
    try {
      await setInboxHandlingStatus({
        itemType: item.itemType,
        itemId: item.id,
        handlingStatus: 'resolved',
      })
      await refreshUnifiedFeed()
      await fetchConversations(true)
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Failed to resolve'))
    }
  }

  async function handleFeedItemLifecycle(item: InboxFeedItem, action: ConversationAction) {
    try {
      if (item.itemType === 'conversation') {
        const convo = conversations.find((c) => c.id === item.id)
        if (convo) await handleConversationAction(convo, action)
      } else {
        const nextState = STATE_AFTER_ACTION[action]
        if (action === 'spam' || action === 'not_spam') {
          await setPhoneCallLogState(item.id, action === 'spam' ? 'spam' : 'active')
        } else {
          await setPhoneCallLogState(item.id, nextState)
        }
        if (selectedCallItem?.id === item.id && nextState !== stateFilter) {
          setSelectedCallItem(null)
          setMobileShowMessages(false)
        }
        await refreshUnifiedFeed()
      }
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Action failed'))
    }
  }

  async function toggleFeedItemLabel(
    item: InboxFeedItem,
    label: ConversationLabel,
    assigned: boolean
  ) {
    if (item.itemType === 'conversation') {
      await toggleConversationLabel(item.id, label, assigned)
      return
    }
    try {
      if (assigned) await removeInboxLabel('phone_call_log', item.id, label.id)
      else await addInboxLabel('phone_call_log', item.id, label.id)
      await fetchInboxLabels()
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Failed to update label'))
    }
  }

  async function createAndAssignFeedLabel(item: InboxFeedItem, name: string) {
    if (item.itemType === 'conversation') {
      await createAndAssignLabel(item.id, name)
      return
    }
    try {
      const color = LABEL_COLORS[inboxLabels.length % LABEL_COLORS.length]
      const label = await createConversationLabel(name, color)
      setInboxLabels((prev) =>
        [...prev, label].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name))
      )
      await addInboxLabel('phone_call_log', item.id, label.id)
      await fetchInboxLabels()
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Failed to create label'))
    }
  }

  async function handleFeedAssign(item: InboxFeedItem, userId: string | null) {
    try {
      await assignInboxItem({ itemType: item.itemType, itemId: item.id, assigneeUserId: userId })
      await refreshUnifiedFeed()
      await fetchConversations(true)
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Assignment failed'))
    }
  }

  function selectFeedItem(item: InboxFeedItem) {
    setSelectedFeedKey(inboxItemKey(item.itemType, item.id))
    setSelectedMessageIds([])
    setShareModalOpen(false)
    setSplitModal(null)
    setSuggestAssignee(null)
    if (item.itemType === 'conversation') {
      const convo = conversations.find((c) => c.id === item.id)
      if (convo) selectConversation(convo)
      setSelectedCallItem(null)
    } else {
      setSelectedConvo(null)
      setSelectedCallItem(item)
      setMobileShowMessages(true)
    }
  }

  function changeViewFilter(value: InboxViewFilter) {
    setViewFilter(value)
    localStorage.setItem('inbox.viewFilter', value)
  }

  function toggleGroupByCustomer() {
    setGroupByCustomer((v) => {
      const next = !v
      localStorage.setItem('inbox.groupByCustomer', next ? '1' : '0')
      return next
    })
  }

  const customerNameById = useMemo(
    () => new Map(customers.map((c) => [c.id, c.company_name || 'Unnamed'])),
    [customers]
  )

  const groupedConvos = useMemo(
    () => (groupByCustomer ? groupConversationsByCustomer(filteredConvos, customerNameById) : null),
    [groupByCustomer, filteredConvos, customerNameById]
  )

  // Conversations per label (all states), for the filter dropdown counts
  const labelCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const c of conversations) {
      for (const id of labelIdsByConvo[c.id] || []) counts[id] = (counts[id] || 0) + 1
    }
    return counts
  }, [conversations, labelIdsByConvo])

  /** Labels currently on a conversation, resolved and in catalog order. */
  function convoLabels(convoId: string): ConversationLabel[] {
    const ids = labelIdsByConvo[convoId] || []
    if (ids.length === 0) return []
    return inboxLabels.filter((l) => ids.includes(l.id))
  }

  // Other separate threads linked to the same customer (for the quick-switch panel)
  const otherCustomerConvos = useMemo(() => {
    if (!selectedConvo?.customer_id) return []
    return conversations.filter(
      (c) =>
        c.customer_id === selectedConvo.customer_id &&
        c.id !== selectedConvo.id &&
        convoState(c) !== 'deleted' &&
        convoState(c) !== 'spam'
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, selectedConvo?.customer_id, selectedConvo?.id])

  /** Open threads for the same contact (phone/email) — for multi-thread banner */
  const openSiblingThreads = useMemo(() => {
    if (!selectedConvo) return []
    const contact = selectedConvo.contact_info
    if (!contact) return []
    return conversations.filter(
      (c) =>
        c.id !== selectedConvo.id &&
        c.contact_info === contact &&
        convoState(c) === 'active' &&
        (c.handling_status || 'open') !== 'resolved'
    )
  }, [conversations, selectedConvo?.id, selectedConvo?.contact_info])

  const openThreadCount = openSiblingThreads.length + (selectedConvo ? 1 : 0)

  useEffect(() => {
    if (!selectedConvo || selectedCallItem) {
      if (!selectedCallItem) setTaskSuggest(null)
      return
    }
    const key = `task:conversation:${selectedConvo.id}`
    if (dismissedSuggests[key] || selectedConvo.handling_status === 'resolved') {
      setTaskSuggest(null)
      return
    }
    if (selectedConvo.channel !== 'sms' || messages.length === 0) {
      setTaskSuggest(null)
      return
    }
    const sorted = [...messages].sort((a, b) => a.created_at.localeCompare(b.created_at))
    const lastInbound = [...sorted].reverse().find((m) => m.direction === 'inbound')
    if (!lastInbound) {
      setTaskSuggest(null)
      return
    }
    const hasReplyAfter = sorted.some(
      (m) => m.direction === 'outbound' && m.created_at > lastInbound.created_at
    )
    if (hasReplyAfter) {
      setTaskSuggest(null)
      return
    }
    const hoursSince = (Date.now() - new Date(lastInbound.created_at).getTime()) / 3600000
    if (hoursSince < 4) {
      setTaskSuggest(null)
      return
    }
    setTaskSuggest({
      title: 'Unanswered SMS',
      description: `Customer SMS from ${formatFullTime(lastInbound.created_at)} has no reply after 4+ hours.`,
      actionType: 'respond_sms',
    })
  }, [selectedConvo, selectedCallItem, messages, dismissedSuggests])

  useEffect(() => {
    if (!selectedCallItem) {
      setCallHasNotes(false)
      return
    }
    let cancelled = false
    countInboxItemNotes('phone_call_log', selectedCallItem.id)
      .then((count) => {
        if (!cancelled) setCallHasNotes(count > 0)
      })
      .catch(() => {
        if (!cancelled) setCallHasNotes(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedCallItem?.id])

  function handleInboxNotesChanged() {
    if (selectedCallItem) {
      countInboxItemNotes('phone_call_log', selectedCallItem.id)
        .then((c) => setCallHasNotes(c > 0))
        .catch(() => {})
    }
  }

  useEffect(() => {
    if (!selectedCallItem) return
    const key = `task:call:${selectedCallItem.id}`
    if (dismissedSuggests[key] || selectedCallItem.handlingStatus === 'resolved') {
      setTaskSuggest(null)
      return
    }
    const isMissed =
      selectedCallItem.direction === 'incoming' &&
      (!selectedCallItem.durationSeconds || selectedCallItem.durationSeconds === 0)
    const noNotes = !callHasNotes
    if (isMissed && noNotes) {
      setTaskSuggest({
        title: 'Missed call — add notes?',
        description: 'Create a follow-up task to call this customer back.',
        actionType: 'callback',
      })
    } else {
      setTaskSuggest(null)
    }
  }, [selectedCallItem, dismissedSuggests, callHasNotes])

  // Spam / archived / trashed conversations don't count toward badges
  const totalUnread = conversations.reduce(
    (sum, c) => sum + (convoState(c) === 'active' ? c.unread_count || 0 : 0),
    0
  )
  const unlinkedCount = conversations.filter((c) => convoState(c) === 'active' && isUnlinked(c)).length
  const stateCounts = useMemo(() => {
    const counts: Record<ConversationState, number> = { active: 0, archived: 0, spam: 0, deleted: 0 }
    for (const c of conversations) counts[convoState(c)]++
    return counts
  }, [conversations])

  // -- Compose Modal --
  // NOTE: ComposeModal, LinkPickerPopover and AssignPanel are render helpers invoked as
  // plain functions (e.g. `ComposeModal()`), NOT as JSX (`<ComposeModal />`). Rendering
  // them as JSX gives them a new component identity on every Inbox re-render, which
  // remounts their DOM and makes inputs lose focus after each keystroke.
  function ComposeModal() {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900">New Message</h3>
            <button onClick={resetCompose} className="p-1 rounded-lg hover:bg-gray-200 transition-colors">
              <XIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {/* Channel selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Channel</label>
              <div className="flex gap-2">
                {(['whatsapp', 'sms', 'email'] as const).map((ch) => {
                  const meta = CHANNEL_META[ch]
                  const Icon = meta.icon
                  const active = composeChannel === ch
                  return (
                    <button
                      key={ch}
                      onClick={() => setComposeChannel(ch)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                        active
                          ? `${meta.bg} ${meta.color} ${meta.border} ring-2 ring-offset-1 ring-current`
                          : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {meta.label}
                    </button>
                  )
                })}
              </div>
              {composeChannel === 'whatsapp' && (
                <p className="mt-1.5 text-xs text-amber-600">
                  WhatsApp sending is not yet connected — the message will be logged only.
                </p>
              )}
            </div>

            {/* From number (SMS only) — shown when multiple lines; single line auto-uses default */}
            {composeChannel === 'sms' && smsFromNumbers.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Send From</label>
                <select
                  value={composeFrom}
                  onChange={(e) => setComposeFrom(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                >
                  {smsFromNumbers.map((n) => (
                    <option key={n} value={n}>
                      {formatPhoneForDisplay(n)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {composeChannel === 'sms' && smsFromNumbers.length === 1 && (
              <p className="text-xs text-gray-500">
                Sending from {formatPhoneForDisplay(smsFromNumbers[0])}
              </p>
            )}
            {composeChannel === 'sms' && smsFromNumbers.length === 0 && (
              <p className="text-xs text-amber-600">
                No SMS from-numbers are configured on the server. Contact an admin.
              </p>
            )}

            {/* From mailbox/alias (email only) */}
            {composeChannel === 'email' && fromOptions.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Send From</label>
                <select
                  value={composeEmailFrom}
                  onChange={(e) => setComposeEmailFrom(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                >
                  {fromOptions.map((o) => (
                    <option key={o.address} value={o.address}>
                      {o.label}
                      {o.personal ? ' (personal)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Recipient */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {composeChannel === 'email' ? 'Email Address' : 'Phone Number'}
              </label>
              <input
                type={composeChannel === 'email' ? 'email' : 'tel'}
                value={composeRecipient}
                onChange={(e) => setComposeRecipient(e.target.value)}
                placeholder={composeChannel === 'email' ? 'recipient@example.com' : '+1 (555) 000-0000'}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Subject (email only) */}
            {composeChannel === 'email' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Subject</label>
                <input
                  type="text"
                  value={composeSubject}
                  onChange={(e) => setComposeSubject(e.target.value)}
                  placeholder="Email subject..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            )}

            {/* Customer association */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Link to Customer <span className="text-gray-400">(optional)</span>
              </label>
              <select
                value={composeCustomerId}
                onChange={(e) => setComposeCustomerId(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="">None</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Body */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Message</label>
              <textarea
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                placeholder="Type your message..."
                rows={5}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
            <button
              onClick={resetCompose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleComposeSend}
              disabled={composeSending || !composeRecipient.trim() || !composeBody.trim()}
              className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <SendIcon className="w-4 h-4" />
              {composeSending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // -- Empty State --
  function EmptyState() {
    return (
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="mx-auto w-20 h-20 rounded-2xl bg-blue-50 flex items-center justify-center mb-6">
            <InboxIcon className="w-10 h-10 text-blue-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No conversations yet</h2>
          <p className="text-gray-500 mb-8">
            Start communicating with your customers via WhatsApp, SMS, or Email. All conversations will appear here.
          </p>
          <button
            onClick={() => setComposeOpen(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200 mb-6"
          >
            <PlusIcon className="w-5 h-5" />
            Compose Message
          </button>
          <div className="flex items-center justify-center gap-3">
            {(['whatsapp', 'sms', 'email'] as const).map((ch) => {
              const meta = CHANNEL_META[ch]
              const Icon = meta.icon
              return (
                <button
                  key={ch}
                  onClick={() => {
                    setComposeChannel(ch)
                    setComposeOpen(true)
                  }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border ${meta.border} ${meta.bg} ${meta.color} text-sm font-medium hover:shadow-md transition-all`}
                >
                  <Icon className="w-4 h-4" />
                  Send {meta.label}
                </button>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  // -- No Conversation Selected State --
  function NoConvoSelected() {
    return (
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center max-w-sm mx-auto px-6">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-5">
            <MessageSquareIcon className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Select a conversation</h3>
          <p className="text-gray-500 text-sm">
            Choose a conversation from the left panel to view messages, or start a new one.
          </p>
        </div>
      </div>
    )
  }

  // -- Conversation List Item --
  function ConvoListItem({ convo }: { convo: Conversation }) {
    const meta = CHANNEL_META[convo.channel]
    const Icon = meta.icon
    const isActive = selectedConvo?.id === convo.id
    const rowLabels = convoLabels(convo.id)

    const secondaryChips: Array<{ key: string; label: string; className: string }> = []
    if (isUnlinked(convo)) {
      secondaryChips.push({ key: 'unlinked', label: 'Unlinked', className: 'text-amber-700 bg-amber-50' })
    }
    for (const label of rowLabels) {
      secondaryChips.push({ key: label.id, label: label.name, className: 'text-gray-600 bg-gray-100' })
    }
    const visibleChips = secondaryChips.slice(0, 2)
    const hiddenChipCount = secondaryChips.length - visibleChips.length

    const preview =
      convo.channel === 'email' && convo.subject
        ? convo.subject
        : convo.last_message || 'No messages yet'

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => selectConversation(convo)}
        onKeyDown={(e) => e.key === 'Enter' && selectConversation(convo)}
        className={`group relative w-full flex items-center gap-2 px-3 py-2 text-left cursor-pointer transition-colors ${
          isActive ? 'bg-blue-50 border-l-2 border-l-blue-600' : 'hover:bg-gray-50 border-l-2 border-l-transparent'
        }`}
      >
        <div className="flex-shrink-0 w-4 flex items-center justify-center">
          <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={`flex-1 text-sm truncate ${convo.unread_count > 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}
            >
              {convo.contact_name || convo.contact_info || 'Unknown'}
            </span>
            <span className="flex items-center flex-shrink-0 gap-1">
              {convo.unread_count > 0 && (
                <span className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0" title="Unread" />
              )}
              <span className="text-[11px] text-gray-400 tabular-nums">{formatTime(convo.last_message_at)}</span>
              <InboxListItemActionsMenu
                item={conversationToFeedItem(convo)}
                assignees={assignees}
                canAssignOthers={canAssignOthers}
                currentUserId={currentUserId}
                onLink={() => openLinkPickerForItem(conversationToFeedItem(convo))}
                onAssign={(userId) => handleFeedAssign(conversationToFeedItem(convo), userId)}
                onLabel={() =>
                  setLabelPickerFor({ itemType: 'conversation', itemId: convo.id, anchor: 'row' })
                }
                onResolve={() => handleFeedItemResolve(conversationToFeedItem(convo))}
                onLifecycleAction={(action) => handleConversationAction(convo, action)}
              />
            </span>
          </div>
          <p
            className={`text-xs truncate ${convo.unread_count > 0 ? 'text-gray-800 font-medium' : 'text-gray-500'}`}
          >
            {preview}
          </p>
          {secondaryChips.length > 0 && (
            <div className="hidden group-hover:flex items-center gap-1 mt-0.5 flex-wrap">
              {visibleChips.map((chip) => (
                <span
                  key={chip.key}
                  className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${chip.className}`}
                >
                  {chip.label}
                </span>
              ))}
              {hiddenChipCount > 0 && (
                <span className="text-[10px] text-gray-400">+{hiddenChipCount}</span>
              )}
            </div>
          )}
        </div>

      </div>
    )
  }

  // -- Message Bubble --
  function MessageBubble({ msg }: { msg: Message }) {
    const outbound = msg.direction === 'outbound'
    const attachments = attachmentsByMessage[msg.id] || []
    const senderLabel = outbound
      ? msg.sent_by && msg.sent_by === currentUserId
        ? 'You'
        : msg.sent_by_name || 'Office'
      : selectedConvo?.contact_name || selectedConvo?.contact_info || 'Customer'

    // HTML emails: swap cid: references for signed URLs of the stored inline
    // images, then sanitize. Attachments rendered in-place aren't repeated below.
    const renderedInline = new Set<string>()
    let emailHtml: string | null = null
    if (msg.body_html) {
      const cidUrls: Record<string, string> = {}
      for (const att of attachments) {
        if (!att.content_id) continue
        const url = attachmentUrls[att.id]
        if (url && msg.body_html.includes(`cid:${att.content_id}`)) {
          cidUrls[att.content_id] = url
          renderedInline.add(att.id)
        }
      }
      emailHtml = sanitizeEmailHtml(msg.body_html, cidUrls)
    }

    const imageAttachments = attachments.filter(
      (att) => !renderedInline.has(att.id) && (att.mime_type || '').startsWith('image/') && attachmentUrls[att.id]
    )
    const fileAttachments = attachments.filter(
      (att) => !renderedInline.has(att.id) && !imageAttachments.includes(att)
    )

    return (
      <div className={`flex ${outbound ? 'justify-end' : 'justify-start'} mb-3 group`}>
        <div className="flex items-start gap-1.5 max-w-[85%]">
          {!outbound && (
            <input
              type="checkbox"
              checked={selectedMessageIds.includes(msg.id)}
              onChange={() => toggleMessageSelection(msg.id)}
              className="mt-3 w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0 opacity-60 group-hover:opacity-100"
              title="Select for sharing"
            />
          )}
          <div className="relative flex-1 min-w-0">
            <div
              className={`${emailHtml ? 'max-w-full' : ''} rounded-2xl px-4 py-2.5 ${
                outbound
                  ? 'bg-blue-600 text-white rounded-br-md'
                  : 'bg-white text-gray-900 border border-gray-200 rounded-bl-md shadow-sm'
              }`}
            >
          <p className={`text-[10px] mb-0.5 ${outbound ? 'text-blue-100/90' : 'text-gray-500'}`}>
            {senderLabel}
          </p>
          {msg.subject && (
            <p className={`text-xs font-semibold mb-1 ${outbound ? 'text-blue-100' : 'text-gray-500'}`}>
              {msg.subject}
            </p>
          )}
          {emailHtml ? (
            <div
              className="text-sm leading-relaxed max-w-full overflow-x-auto break-words prose prose-sm prose-gray max-w-none [&_img]:max-w-full [&_img]:h-auto [&_a]:text-blue-600 [&_a]:underline [&_table]:max-w-full [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2"
              dangerouslySetInnerHTML={{ __html: emailHtml }}
            />
          ) : (
            <p className="text-sm whitespace-pre-wrap break-words">{msg.body}</p>
          )}
          {imageAttachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {imageAttachments.map((att) => (
                <img
                  key={att.id}
                  src={attachmentUrls[att.id]}
                  alt={att.file_name}
                  title={att.file_name}
                  onClick={() => handleDownloadAttachment(att)}
                  className="max-h-48 max-w-full rounded-lg border border-black/10 cursor-pointer object-contain bg-white"
                />
              ))}
            </div>
          )}
          {fileAttachments.length > 0 && (
            <div className="mt-1.5 space-y-1">
              {fileAttachments.map((att) => (
                <button
                  key={att.id}
                  onClick={() => handleDownloadAttachment(att)}
                  className={`flex items-center gap-1.5 text-xs underline ${outbound ? 'text-blue-100' : 'text-blue-600'}`}
                >
                  <FileIcon className="w-3.5 h-3.5" />
                  {att.file_name}
                </button>
              ))}
            </div>
          )}
          <div className={`flex items-center justify-end gap-1 mt-1 ${outbound ? 'text-blue-200' : 'text-gray-400'}`}>
            <span className="text-[10px]">{formatFullTime(msg.created_at)}</span>
            {outbound && statusIcon(msg.status)}
          </div>
            </div>
            <div className="absolute -top-1 right-0 hidden group-hover:flex items-center gap-0.5 bg-white border border-gray-200 rounded-lg shadow-sm p-0.5">
              <button
                type="button"
                onClick={() =>
                  setSplitModal({
                    messageId: msg.id,
                    preview: msg.body || msg.subject || '',
                  })
                }
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
                title="Split conversation here"
              >
                <ScissorsIcon className="w-3.5 h-3.5" />
              </button>
              {openSiblingThreads.length > 0 && (
                <div className="relative">
                  <select
                    disabled={movingMessageId === msg.id}
                    defaultValue=""
                    onChange={(e) => {
                      const targetId = e.target.value
                      if (targetId) handleMoveMessage(msg.id, targetId)
                      e.target.value = ''
                    }}
                    className="text-[10px] border-0 bg-transparent py-1 pl-1 pr-4 text-gray-600 cursor-pointer max-w-[6rem]"
                    title="Move to another open thread"
                  >
                    <option value="" disabled>
                      Move to…
                    </option>
                    {openSiblingThreads.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.subject || c.contact_name || formatTime(c.last_message_at)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // -- Link Picker Modal --
  function LinkPickerModal() {
    if (!linkPickerOpen) return null
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="relative">
          <InboxEntityLinkPopover
            key={linkPickerDefaultKey}
            defaultEntityKey={linkPickerDefaultKey}
            onSelect={linkConversationTo}
            onClose={() => setLinkPickerOpen(false)}
          />
        </div>
      </div>
    )
  }

  // -- Main Render --
  const shellHeight = embedded ? 'h-full min-h-0' : 'h-[calc(100vh-4rem)]'

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${shellHeight}`}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-[3px] border-gray-200 border-t-blue-600 mb-4" />
          <p className="text-gray-500 text-sm">Loading inbox...</p>
        </div>
      </div>
    )
  }

  const hasConversations = conversations.length > 0

  return (
    <div className={`${shellHeight} flex flex-col bg-white`}>
      {composeOpen && ComposeModal()}
      {splitModal && selectedConvo && (
        <SplitConversationModal
          isOpen
          conversationId={selectedConvo.id}
          messageId={splitModal.messageId}
          messagePreview={splitModal.preview}
          onClose={() => setSplitModal(null)}
          onSplit={(newId) => {
            fetchConversations()
            const convo = conversations.find((c) => c.id === newId)
            if (convo) selectConversation(convo)
          }}
        />
      )}
      {shareModalOpen && selectedConvo && (
        <ShareMessagesModal
          isOpen
          conversationId={selectedConvo.id}
          messageIds={selectedMessageIds}
          assignees={assignees}
          onClose={() => setShareModalOpen(false)}
          onShared={() => setSelectedMessageIds([])}
        />
      )}
      {resolveTaskWarning !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Resolve with open tasks?</h3>
            <p className="text-sm text-gray-600">
              This conversation has {resolveTaskWarning} open task{resolveTaskWarning === 1 ? '' : 's'}{' '}
              still incomplete. You can resolve anyway — tasks will remain open.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setResolveTaskWarning(null)
                  setPendingHandling(null)
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmResolveDespiteOpenTasks}
                className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Resolve anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top bar with channel tabs */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-white">
        <div
          className={`flex items-center justify-between gap-1.5 ${
            embedded ? 'px-2 lg:px-3 py-1' : 'px-4 lg:px-6 py-3'
          }`}
        >
          <div className="flex items-center gap-1.5 min-w-0 shrink-0">
            {mobileShowMessages && selectedConvo && (
              <button
                onClick={() => setMobileShowMessages(false)}
                className="lg:hidden p-1 -ml-1 rounded-lg hover:bg-gray-100"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
              </button>
            )}
            {!embedded && (
              <>
                <h1 className="text-xl font-bold text-gray-900">Inbox</h1>
                {totalUnread > 0 && (
                  <span className="min-w-[22px] h-[22px] px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {totalUnread > 99 ? '99+' : totalUnread}
                  </span>
                )}
              </>
            )}
            {embedded && embeddedLeading}
            {embedded && embeddedLeading && (
              <div className="hidden sm:block w-px h-5 bg-gray-200 flex-shrink-0 mx-0.5" aria-hidden />
            )}
            {embedded && !embeddedLeading && totalUnread > 0 && (
              <span
                className="min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shrink-0"
                title={`${totalUnread} unread`}
              >
                {totalUnread > 99 ? '99+' : totalUnread}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1 min-w-0 flex-1 justify-end">
            {/* Mode toggle — fixed position, never shifts */}
            <div className="hidden sm:flex items-center bg-gray-100 rounded-md p-0.5 flex-shrink-0">
              <button
                onClick={() => setInboxMode('unified')}
                className={`px-2 py-0.5 text-xs font-medium rounded transition-all ${
                  inboxMode === 'unified' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                All comms
              </button>
              <button
                onClick={() => {
                  setInboxMode('tasks')
                  setSelectedConvo(null)
                  setSelectedCallItem(null)
                  setMobileShowMessages(false)
                }}
                className={`px-2 py-0.5 text-xs font-medium rounded transition-all flex items-center gap-1 ${
                  inboxMode === 'tasks' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <ListTodoIcon className="w-3.5 h-3.5" />
                Tasks
              </button>
            </div>

            {/* Unified-only filters — invisible on Tasks tab but keeps layout width */}
            <div
              className={`hidden sm:flex items-center gap-1 flex-nowrap justify-end min-w-0 overflow-x-auto ${
                inboxMode === 'tasks' ? 'invisible pointer-events-none' : ''
              }`}
              aria-hidden={inboxMode === 'tasks'}
            >
            <div className="flex items-center bg-gray-100 rounded-md p-0.5 flex-shrink-0">
              {(['all', 'whatsapp', 'sms', 'email', 'call'] as const).map((ch) => {
                const active = channelFilter === ch
                const label =
                  ch === 'all' ? 'All' : ch === 'call' ? 'Calls' : CHANNEL_META[ch].label
                return (
                  <button
                    key={ch}
                    onClick={() => setChannelFilter(ch)}
                    className={`px-2 py-0.5 text-xs font-medium rounded transition-all whitespace-nowrap ${
                      active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {label}
                  </button>
                )
              })}
            </div>

            {/* View: Inbox (active) / Archived / Spam / Trash */}
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value as ConversationState)}
              className="hidden sm:block px-1.5 py-0.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-md bg-white focus:ring-2 focus:ring-blue-500"
              title="Which conversations to show"
            >
              {STATE_VIEWS.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.value === 'active' ? v.label : `${v.label} (${stateCounts[v.value]})`}
                </option>
              ))}
            </select>

            {/* Label filter (applies in every view) */}
            {inboxLabels.length > 0 && (
              <select
                value={labelFilter}
                onChange={(e) => changeLabelFilter(e.target.value)}
                className="hidden sm:block px-1.5 py-0.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-md bg-white focus:ring-2 focus:ring-blue-500"
                title="Filter by label"
              >
                <option value="">All labels</option>
                {inboxLabels.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} ({labelCounts[l.id] || 0})
                  </option>
                ))}
              </select>
            )}

            <select
              value={viewFilter}
              onChange={(e) => changeViewFilter(e.target.value as InboxViewFilter)}
              className="hidden sm:block px-1.5 py-0.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-md bg-white focus:ring-2 focus:ring-blue-500"
              title="Inbox view"
            >
              {INBOX_VIEW_FILTERS.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </select>

            <button
              onClick={() => setStaffUnassignedOnly((v) => !v)}
              className={`hidden sm:flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                staffUnassignedOnly ? 'bg-gray-800 text-white' : 'text-gray-500 hover:bg-gray-100 border border-transparent'
              }`}
            >
              Staff unassigned ({staffUnassignedCount})
            </button>

            <button
              onClick={() => setUnlinkedOnly((v) => !v)}
              className={`hidden sm:flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                unlinkedOnly ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'text-gray-500 hover:bg-gray-100 border border-transparent'
              }`}
            >
              <UserCircleIcon className="w-3.5 h-3.5" />
              Unlinked ({unlinkedCount})
            </button>

            <button
              onClick={toggleGroupByCustomer}
              title="Group conversations by customer (threads stay separate)"
              className={`hidden sm:flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                groupByCustomer ? 'bg-blue-100 text-blue-800 border border-blue-300' : 'text-gray-500 hover:bg-gray-100 border border-transparent'
              }`}
            >
              <UsersIcon className="w-3.5 h-3.5" />
              Group
            </button>

            {/* Address filter (email tab): billing@ vs the info@/office@ aliases vs personal mailboxes */}
            {channelFilter === 'email' && fromOptions.length > 1 && (
              <select
                value={emailAddressFilter}
                onChange={(e) => setEmailAddressFilter(e.target.value)}
                className="hidden sm:block px-1.5 py-0.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-md bg-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All addresses</option>
                {fromOptions.map((o) => (
                  <option key={o.address} value={o.address}>
                    {o.address}
                  </option>
                ))}
              </select>
            )}

            <button
              onClick={handleSyncEmail}
              disabled={syncingEmail}
              className="hidden sm:flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
              title="Pull new messages from IMAP mailbox"
            >
              <MailIcon className="w-3.5 h-3.5" />
              {syncingEmail ? 'Syncing…' : 'Sync email'}
            </button>

            <button
              onClick={() => setComposeOpen(true)}
              className="flex items-center gap-1 px-2.5 py-0.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors shadow-sm whitespace-nowrap"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Compose</span>
            </button>
            </div>
          </div>
        </div>

        {/* Mobile channel filter */}
        <div className="sm:hidden flex items-center gap-1 px-3 pb-1.5 overflow-x-auto">
          <button
            onClick={() => setInboxMode('unified')}
            className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
              inboxMode === 'unified' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All comms
          </button>
          <button
            onClick={() => {
              setInboxMode('tasks')
              setSelectedConvo(null)
              setSelectedCallItem(null)
              setMobileShowMessages(false)
            }}
            className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-full transition-all flex items-center gap-1 ${
              inboxMode === 'tasks' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <ListTodoIcon className="w-3.5 h-3.5" />
            Tasks
          </button>
          <div
            className={`flex items-center gap-1 flex-shrink-0 ${
              inboxMode === 'tasks' ? 'invisible pointer-events-none' : ''
            }`}
            aria-hidden={inboxMode === 'tasks'}
          >
          {(['all', 'whatsapp', 'sms', 'email', 'call'] as const).map((ch) => {
            const active = channelFilter === ch
            const label = ch === 'all' ? 'All' : ch === 'call' ? 'Calls' : CHANNEL_META[ch].label
            return (
              <button
                key={ch}
                onClick={() => setChannelFilter(ch)}
                className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                  active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            )
          })}
          <button
            onClick={() => setUnlinkedOnly((v) => !v)}
            className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
              unlinkedOnly ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Unlinked ({unlinkedCount})
          </button>
          <button
            onClick={toggleGroupByCustomer}
            className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
              groupByCustomer ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Group
          </button>
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value as ConversationState)}
            className="flex-shrink-0 px-2 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-full bg-gray-100"
          >
            {STATE_VIEWS.map((v) => (
              <option key={v.value} value={v.value}>
                {v.value === 'active' ? v.label : `${v.label} (${stateCounts[v.value]})`}
              </option>
            ))}
          </select>
          {inboxLabels.length > 0 && (
            <select
              value={labelFilter}
              onChange={(e) => changeLabelFilter(e.target.value)}
              className="flex-shrink-0 px-2 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-full bg-gray-100"
            >
              <option value="">All labels</option>
              {inboxLabels.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({labelCounts[l.id] || 0})
                </option>
              ))}
            </select>
          )}
          </div>
        </div>

        {entityFilterType && entityFilterId && (
          <div className="px-4 lg:px-6 pb-2 text-xs text-gray-500 flex items-center gap-1.5">
            <LinkIcon className="w-3 h-3" />
            Filtered by linked {entityFilterType}
          </div>
        )}
      </div>


            {labelPickerFor?.anchor === 'row' && (() => {
              const rowItem = filteredFeedItems.find(
                (i) => i.itemType === labelPickerFor.itemType && i.id === labelPickerFor.itemId
              )
              if (!rowItem) return null
              const key = inboxItemKey(rowItem.itemType, rowItem.id)
              const ids =
                inboxLabelIdsByEntity[key] ||
                (rowItem.itemType === 'conversation' ? labelIdsByConvo[rowItem.id] : []) ||
                []
              return (
                <div
                  className="fixed inset-0 z-40 flex items-center justify-center bg-black/20 p-4"
                  onClick={() => setLabelPickerFor(null)}
                >
                  <div className="relative" onClick={(e) => e.stopPropagation()}>
                    <LabelPickerPopover
                      labels={inboxLabels}
                      selectedIds={new Set(ids)}
                      onToggle={(label, assigned) => toggleFeedItemLabel(rowItem, label, assigned)}
                      onCreate={(name) => createAndAssignFeedLabel(rowItem, name)}
                      onClose={() => setLabelPickerFor(null)}
                    />
                  </div>
                </div>
              )
            })()}

      {/* Main split pane */}
      {inboxMode === 'tasks' ? (
        <InboxTasksTab
          currentUserId={currentUserId}
          currentUserRole={profile?.role || null}
          assignees={assignees}
          onOpenSource={(sourceType, sourceId) => {
            setInboxMode('unified')
            if (sourceType === 'conversation') {
              const convo = conversations.find((c) => c.id === sourceId)
              if (convo) selectConversation(convo)
            } else {
              const item = feedItems.find((f) => f.itemType === 'phone_call_log' && f.id === sourceId)
              if (item) selectFeedItem(item)
            }
          }}
        />
      ) : !hasConversations && filteredFeedItems.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {/* Left sidebar: conversation list */}
          <div
            className={`w-full lg:w-[400px] flex-shrink-0 border-r border-gray-200 flex flex-col bg-white ${
              mobileShowMessages ? 'hidden lg:flex' : 'flex'
            }`}
          >
            {/* Search + sort */}
            <div className={`flex-shrink-0 border-b border-gray-100 flex items-center gap-2 ${embedded ? 'p-2' : 'p-3'}`}>
              <div className="relative flex-1 min-w-0">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-colors"
                />
              </div>
              <select
                value={sortBy}
                onChange={(e) => changeSortBy(e.target.value as InboxSortKey)}
                title="Sort conversations"
                aria-label="Sort conversations"
                className="flex-shrink-0 max-w-[9rem] px-2 py-2 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors"
              >
                {INBOX_SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Conversation list */}
            <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
              {filteredFeedItems.length === 0 ? (
                <div className="p-8 text-center">
                  <SearchIcon className="mx-auto w-8 h-8 text-gray-300 mb-3" />
                  <p className="text-sm text-gray-500">No items found</p>
                </div>
              ) : groupByCustomer && groupedConvos && filteredConvos.length > 0 ? (
                <>
                  {groupedConvos.unassigned.length > 0 && (
                    <div>
                      <div className="px-4 py-1.5 bg-amber-50/70 text-[11px] font-semibold uppercase tracking-wider text-amber-700">
                        Unlinked
                      </div>
                      {groupedConvos.unassigned.map((convo) => (
                        <ConvoListItem key={convo.id} convo={convo} />
                      ))}
                    </div>
                  )}
                  {groupedConvos.groups.map((group) => {
                    const expanded =
                      expandedGroups[group.customerId] ??
                      group.conversations.some((c) => c.id === selectedConvo?.id)
                    return (
                      <div key={group.customerId}>
                        <button
                          onClick={() =>
                            setExpandedGroups((prev) => ({ ...prev, [group.customerId]: !expanded }))
                          }
                          className="w-full flex items-center gap-2 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-left transition-colors"
                        >
                          {expanded ? (
                            <ChevronDownIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          ) : (
                            <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          )}
                          <span className="flex-1 min-w-0 text-sm font-semibold text-gray-800 truncate">
                            {group.customerName}
                          </span>
                          <span className="text-[11px] text-gray-400 flex-shrink-0">
                            {group.conversations.length}
                          </span>
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            {formatTime(group.latestActivity)}
                          </span>
                          {group.unreadCount > 0 && (
                            <span className="flex-shrink-0 min-w-[20px] h-5 px-1.5 bg-blue-600 text-white text-[11px] font-bold rounded-full flex items-center justify-center">
                              {group.unreadCount > 99 ? '99+' : group.unreadCount}
                            </span>
                          )}
                        </button>
                        {expanded &&
                          group.conversations.map((convo) => (
                            <ConvoListItem key={convo.id} convo={convo} />
                          ))}
                      </div>
                    )
                  })}
                </>
              ) : (
                filteredFeedItems.map((item) => (
                  <UnifiedInboxListItem
                    key={inboxItemKey(item.itemType, item.id)}
                    item={item}
                    active={selectedFeedKey === inboxItemKey(item.itemType, item.id)}
                    labels={inboxLabels}
                    labelIds={inboxLabelIdsByEntity[inboxItemKey(item.itemType, item.id)] || labelIdsByConvo[item.id] || []}
                    assignees={assignees}
                    canAssignOthers={canAssignOthers}
                    currentUserId={currentUserId}
                    onSelect={selectFeedItem}
                    onLink={openLinkPickerForItem}
                    onAssign={handleFeedAssign}
                    onLabel={(feedItem) =>
                      setLabelPickerFor({ itemType: feedItem.itemType, itemId: feedItem.id, anchor: 'row' })
                    }
                    onResolve={handleFeedItemResolve}
                    onLifecycleAction={handleFeedItemLifecycle}
                  />
                ))
              )}
            </div>
          </div>

          {/* Right panel: messages */}
          <div
            className={`flex-1 flex flex-col bg-gray-50 ${
              !mobileShowMessages ? 'hidden lg:flex' : 'flex'
            }`}
          >
            {!selectedConvo && !selectedCallItem ? (
              <NoConvoSelected />
            ) : selectedCallItem ? (
              <div className="flex flex-col min-h-0 overflow-hidden flex-1">
                {taskSuggest && (
                  <InboxSuggestBanner
                    title={taskSuggest.title}
                    description={taskSuggest.description}
                    onConfirm={confirmTaskSuggest}
                    onDismiss={() => dismissSuggest(`task:call:${selectedCallItem.id}`)}
                  />
                )}
                <InboxCallDetailPanel
                item={selectedCallItem}
                assignees={assignees}
                canAssignOthers={canAssignOthers}
                currentUserId={currentUserId}
                currentUserRole={profile?.role || null}
                labels={inboxLabels}
                labelIds={
                  inboxLabelIdsByEntity[inboxItemKey('phone_call_log', selectedCallItem.id)] || []
                }
                onUpdated={() => refreshUnifiedFeed()}
                onLabelsChanged={fetchInboxLabels}
                authorName={inboxAuthorName(profile)}
                onNotesChanged={handleInboxNotesChanged}
                onCustomerLinked={async (customerId) => {
                  if (selectedCallItem?.assignedToUserId) return
                  const userId = await suggestAssigneeForCustomer(customerId)
                  if (!userId) return
                  const user = assignees.find((a) => a.id === userId)
                  if (!user) return
                  const key = `assignee:call:${selectedCallItem?.id}`
                  if (dismissedSuggests[key]) return
                  setSuggestAssignee({ userId, name: assigneeDisplayName(user) })
                }}
              />
              </div>
            ) : selectedConvo ? (
                <div className="flex flex-col min-h-0 overflow-hidden flex-1">
                <InboxConversationDetailHeader
                  contactName={selectedConvo.contact_name || selectedConvo.contact_info}
                  contactInfo={selectedConvo.contact_info}
                  subject={selectedConvo.channel === 'email' ? selectedConvo.subject : null}
                  conversationId={selectedConvo.id}
                  handlingStatus={selectedConvo.handling_status || 'open'}
                  waitingUntil={selectedConvo.waiting_until || null}
                  assignedToUserId={selectedConvo.assigned_to_user_id || null}
                  assignees={assignees}
                  canAssignOthers={canAssignOthers}
                  currentUserId={currentUserId}
                  entityLinkFields={ENTITY_LINK_FIELDS}
                  linkedEntityValues={{
                    customer_id: selectedConvo.customer_id,
                    lead_id: selectedConvo.lead_id,
                    property_id: selectedConvo.property_id,
                    contract_id: selectedConvo.contract_id,
                    job_id: selectedConvo.job_id,
                    proposal_id: selectedConvo.proposal_id,
                    employee_id: selectedConvo.employee_id,
                  }}
                  linkedLabels={linkedLabels}
                  labels={inboxLabels}
                  rowLabels={convoLabels(selectedConvo.id)}
                  conversationState={convoState(selectedConvo)}
                  stateBadge={STATE_BADGE[convoState(selectedConvo)] || null}
                  isUnlinked={isUnlinked(selectedConvo)}
                  onAssign={handleConvoAssign}
                  onHandlingChange={handleConvoHandling}
                  onResolve={() => handleConvoHandling('resolved')}
                  onLinkRecord={() => {
                    setLinkPickerDefaultKey('customer_id')
                    setLinkPickerOpen(true)
                  }}
                  onToggleLabelPicker={() =>
                    setLabelPickerFor((v) =>
                      v?.anchor === 'header' && v.itemType === 'conversation' && v.itemId === selectedConvo.id
                        ? null
                        : { itemType: 'conversation', itemId: selectedConvo.id, anchor: 'header' }
                    )
                  }
                  onConversationAction={(action) => handleConversationAction(selectedConvo, action)}
                  onNavigateEntity={(route) => navigate(route)}
                  onUnlinkEntity={unlinkConversation}
                  onOpenUnlinkedModal={() => setUnlinkedModalOpen(true)}
                  openThreads={
                    openThreadCount > 1
                      ? {
                          currentId: selectedConvo.id,
                          threads: [
                            {
                              id: selectedConvo.id,
                              label:
                                selectedConvo.subject ||
                                selectedConvo.contact_name ||
                                'Current thread',
                              unread: selectedConvo.unread_count,
                              sortAt: selectedConvo.last_message_at || selectedConvo.created_at || '',
                            },
                            ...openSiblingThreads.map((c) => ({
                              id: c.id,
                              label:
                                c.subject ||
                                c.contact_name ||
                                formatTime(c.last_message_at),
                              unread: c.unread_count,
                              sortAt: c.last_message_at || c.created_at || '',
                            })),
                          ]
                            .sort((a, b) => (b.sortAt || '').localeCompare(a.sortAt || ''))
                            .map(({ sortAt: _sortAt, ...t }) => t),
                          onSelect: (id: string) => {
                            const convo = conversations.find((c) => c.id === id)
                            if (convo) selectConversation(convo)
                          },
                        }
                      : null
                  }
                  labelPicker={
                    labelPickerFor?.anchor === 'header' && labelPickerFor.itemType === 'conversation' && labelPickerFor.itemId === selectedConvo.id ? (
                      <LabelPickerPopover
                        labels={inboxLabels}
                        selectedIds={new Set(labelIdsByConvo[selectedConvo.id] || [])}
                        onToggle={(label, assigned) =>
                          toggleConversationLabel(selectedConvo.id, label, assigned)
                        }
                        onCreate={(name) => createAndAssignLabel(selectedConvo.id, name)}
                        onClose={() => setLabelPickerFor(null)}
                        className="left-4 top-full mt-1"
                      />
                    ) : undefined
                  }
                />

                {selectedConvo.customer_id && otherCustomerConvos.length > 0 && (
                  <CustomerOtherConversations
                    customerName={customerNameById.get(selectedConvo.customer_id) || 'this customer'}
                    conversations={otherCustomerConvos}
                    onSelect={(id) => {
                      const convo = conversations.find((c) => c.id === id)
                      if (convo) selectConversation(convo)
                    }}
                  />
                )}


                {suggestAssignee && (
                  <InboxSuggestBanner
                    title={`Assign to ${suggestAssignee.name}?`}
                    description="Suggested based on this customer's account manager."
                    confirmLabel="Assign"
                    onConfirm={() => handleConvoAssign(suggestAssignee.userId)}
                    onDismiss={() =>
                      dismissSuggest(`assignee:conversation:${selectedConvo.id}`)
                    }
                  />
                )}

                {taskSuggest && !selectedCallItem && (
                  <InboxSuggestBanner
                    title={taskSuggest.title}
                    description={taskSuggest.description}
                    onConfirm={confirmTaskSuggest}
                    onDismiss={() =>
                      dismissSuggest(`task:conversation:${selectedConvo.id}`)
                    }
                  />
                )}

                {selectedMessageIds.length > 0 && (
                  <div className="flex-shrink-0 mx-4 mt-2 flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2">
                    <span className="text-xs text-gray-600">
                      {selectedMessageIds.length} message{selectedMessageIds.length === 1 ? '' : 's'}{' '}
                      selected
                    </span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedMessageIds([])}
                        className="px-2.5 py-1 text-xs font-medium text-gray-600 hover:underline"
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        onClick={openShareModal}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        <ShareIcon className="w-3.5 h-3.5" />
                        Share selected
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex flex-1 min-h-0 overflow-hidden flex-col lg:flex-row">
                  <div className="flex flex-col flex-1 min-w-0 min-h-0">
                    {/* Messages area */}
                    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
                      {messagesLoading ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="inline-block animate-spin rounded-full h-7 w-7 border-[3px] border-gray-200 border-t-blue-600" />
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                          <p className="text-sm text-gray-400">No messages in this conversation</p>
                        </div>
                      ) : (
                        <>
                          {messages.map((msg) => (
                            <MessageBubble key={msg.id} msg={msg} />
                          ))}
                          <div ref={messagesEndRef} />
                        </>
                      )}
                    </div>

                    {/* Reply box */}
                    <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-3 space-y-2">
                      {selectedConvo.channel === 'sms' && smsFromNumbers.length > 1 && (
                        <div className="flex items-center gap-2">
                          <label className="text-xs font-medium text-gray-600 flex-shrink-0">From</label>
                          <select
                            value={composeFrom}
                            onChange={(e) => setComposeFrom(e.target.value)}
                            className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            {smsFromNumbers.map((n) => (
                              <option key={n} value={n}>
                                {formatPhoneForDisplay(n)}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="flex items-end gap-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          onChange={handleAttachFile}
                          className="hidden"
                        />
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingFile}
                          title="Attach file"
                          className="flex-shrink-0 w-10 h-10 flex items-center justify-center text-gray-500 rounded-xl hover:bg-gray-100 disabled:opacity-40 transition-colors"
                        >
                          {uploadingFile ? <Loader2Icon className="w-4.5 h-4.5 animate-spin" /> : <PaperclipIcon className="w-4.5 h-4.5" />}
                        </button>
                        <div className="flex-1 relative">
                          <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault()
                                handleSendReply()
                              }
                            }}
                            placeholder="Type a message..."
                            rows={1}
                            className="w-full pl-4 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 focus:bg-white transition-colors"
                            style={{ minHeight: '42px', maxHeight: '120px' }}
                          />
                        </div>
                        <button
                          onClick={handleSendReply}
                          disabled={sendingReply || !replyText.trim()}
                          className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <SendIcon className="w-4.5 h-4.5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <InboxDetailSidebar
                    sourceItem={conversationToFeedItem(selectedConvo)}
                    currentUserId={currentUserId}
                    currentUserRole={profile?.role || null}
                    assigneeDefaultId={selectedConvo.assigned_to_user_id || currentUserId}
                    authorName={inboxAuthorName(profile)}
                    onNotesChanged={() => {}}
                  />
                </div>

                <InboxUnlinkedLinkModal
                  isOpen={unlinkedModalOpen}
                  onClose={() => setUnlinkedModalOpen(false)}
                  conversationId={selectedConvo.id}
                  channel={selectedConvo.channel}
                  contactInfo={selectedConvo.contact_info}
                  assigning={assigning}
                  onSearch={searchUnlinkedRecords}
                  onAttachOnly={attachConversationOnly}
                  onSaveAsContact={addAsNewContact}
                  onLinkEntity={(key) => {
                    setLinkPickerDefaultKey(key)
                    setLinkPickerOpen(true)
                    setUnlinkedModalOpen(false)
                  }}
                  onCreateNew={(type) => {
                    const phone = selectedConvo.channel !== 'email' ? selectedConvo.contact_info || '' : ''
                    const email = selectedConvo.channel === 'email' ? selectedConvo.contact_info || '' : ''
                    const base = type === 'customer' ? '/customers/create' : '/leads/create'
                    navigate(
                      `${base}?conversationId=${encodeURIComponent(selectedConvo.id)}&phone=${encodeURIComponent(phone)}&email=${encodeURIComponent(email)}`
                    )
                    setUnlinkedModalOpen(false)
                  }}
                />

                <LinkPickerModal />
                </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  )
}
